import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { runMetrics } from '../../api'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import './OverviewTab.css'
import { findingTabOf, findingCount, isActive, tabFindingCounts, tierOf } from './findings'
import { useAuth } from '../../AuthProvider'

// A source is stale when its newest sync is older than this. Syncs run daily.
const STALE_AFTER_MS = 48 * 3600000

function SystemRow({ system }) {
  const when = system.last_synced ? new Date(system.last_synced) : null
  const stale = when ? (Date.now() - when) > STALE_AFTER_MS : null
  return (
    <tr className="ov-src-row">
      <td className="ov-src-name">{system.label}</td>
      <td className="ov-src-count">{system.tables} {system.tables === 1 ? 'table' : 'tables'}</td>
      <td>{when ? <span className={`ov-src-age ${stale ? 'stale' : 'fresh'}`}>{fmtSyncDate(system.last_synced)}</span> : '—'}</td>
      <td>{stale == null
        ? <span className="ov-src-status">No sync date</span>
        : <span className={`ov-src-status ${stale ? 'stale' : 'ready'}`}>{stale ? 'Stale' : 'Current'}</span>}</td>
    </tr>
  )
}

function fmtSyncDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// An amount in its own currency, compact: "USD 120K". Never converted, never a bare "$".
function fmtAmount(n, currency) {
  if (n == null) return '—'
  const v = n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : `${Math.round(n)}`
  return currency ? `${currency} ${v}` : v
}

// ── Greeting ────────────────────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

// The tab guide and the contact line moved off the landing page (Scott, 2026-10-05): each
// tab's description is its tooltip in the portal tab bar (CustomerPortal/tabGuide.js), and
// the contact line is in the portal footer.
function WelcomeBanner({ userName, companyName, critCount, warnCount, freshness }) {
  const greeting = getGreeting()
  const firstName = userName ? userName.split(' ')[0] : null
  const systems = freshness?.systems || []
  return (
    <div className="ov-greeting ov-welcome">
      <div className="ov-greeting-left">
        <div className="ov-greeting-text">
          {greeting}{firstName ? `, ${firstName}` : ''}.
        </div>
        <div className="ov-greeting-sub">
          Welcome to {companyName ? `${companyName}'s` : 'your'} Sempirical portal.
          {freshness?.as_of ? ` Data as of ${fmtSyncDate(freshness.as_of)}.` : ''}
        </div>
        {systems.length > 0 && (
          <div className="ov-welcome-sources">
            {systems.map(sys => (
              <span key={sys.label} className="ov-welcome-source">
                {sys.label}: {sys.last_synced ? `synced ${fmtSyncDate(sys.last_synced)}` : 'no sync date'}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="ov-greeting-right">
        {critCount > 0 && (
          <div className="ov-greeting-pill ov-gp-critical">{critCount} critical</div>
        )}
        {warnCount > 0 && (
          <div className="ov-greeting-pill ov-gp-warning">{warnCount} warning{warnCount !== 1 ? 's' : ''}</div>
        )}
        {critCount === 0 && warnCount === 0 && (
          <div className="ov-greeting-pill ov-gp-clear">No open findings</div>
        )}
      </div>
    </div>
  )
}

// ── Top Findings Strip ───────────────────────────────────────────────────────
function FindingRow({ obs, onTabSwitch }) {
  const sev = obs.type === 'risk' ? 'critical' : obs.type === 'insight' ? 'warning' : 'info'
  const sevLabel = sev === 'critical' ? 'Critical' : sev === 'warning' ? 'Warning' : 'Info'
  // the check's reviewed subject when it has one ("12 PTO plans"); else people when the scan
  // counted them; otherwise the check's record count, said as records (findings.js)
  const count = findingCount(obs)
  const countText = count ? `${count.value.toLocaleString()} ${count.label}` : null
  return (
    <div
      className={`ov-finding-row ov-finding-${sev}`}
      onClick={() => onTabSwitch(findingTabOf(obs))}
    >
      <span className={`ov-finding-sev ov-fsev-${sev}`}>{sevLabel}</span>
      <span className="ov-finding-title">{obs.title}</span>
      {countText && <span className="ov-finding-count">{countText}</span>}
      <span className="ov-finding-arrow">›</span>
    </div>
  )
}

// ── Metric Spotlight ─────────────────────────────────────────────────────────
function buildSpotlightMetrics(kpis, composition, geo_type, medianSalary, multiState) {
  const metrics = []

  if (kpis.active_count != null)
    metrics.push({ icon: '👥', label: 'Active Employees', value: kpis.active_count.toLocaleString(), sub: `${kpis.state_count || 0} ${geo_type === 'countries' ? 'countries' : 'states'}` })

  if (kpis.total_headcount != null)
    metrics.push({ icon: '📋', label: 'Total Headcount', value: kpis.total_headcount.toLocaleString(),
      sub: kpis.leave_count ? `${(kpis.active_count ?? 0).toLocaleString()} active · ${kpis.leave_count.toLocaleString()} on leave` : 'Active and on leave' })

  if (kpis.pay_group_count != null)
    metrics.push({ icon: '💳', label: 'Pay Groups', value: kpis.pay_group_count, sub: composition?.pay_frequency?.length ? `${composition.pay_frequency.length} frequencies` : null })

  // The Metrics tab's own "Median Annual Salary" card (active salaried employees), not a
  // second calculation of it.
  if (medianSalary?.amount != null)
    metrics.push({ icon: '💰', label: 'Median Salary', value: fmtAmount(medianSalary.amount, medianSalary.currency),
      sub: medianSalary.others > 0
        ? `Salaried, paid in ${medianSalary.currency} · ${medianSalary.others} other ${medianSalary.others === 1 ? 'currency' : 'currencies'} in Metrics`
        : 'Salaried employees' })

  // The Metrics tab's own "Multi-Jurisdiction Employees (US, YTD)" card. The profile's
  // separate count couldn't join for some tenants and sent 0, hiding the tile.
  if (multiState != null && multiState > 0)
    metrics.push({ icon: '🗺️', label: 'Multi-State Employees', value: multiState.toLocaleString(), sub: 'Withheld in 2+ states this year', highlight: multiState > 10 ? 'amber' : null })

  if (kpis.state_count != null)
    metrics.push({ icon: '📍', label: geo_type === 'countries' ? 'Countries' : 'States', value: kpis.state_count, sub: 'Geographic footprint' })

  if (kpis.job_code_count != null)
    metrics.push({ icon: '🏷️', label: 'Job Codes', value: kpis.job_code_count, sub: null })

  // One tile per org level the customer's system defines, under its own name, with what it
  // finds: how many units, and how many current employees carry a code at that level.
  const plural = (l) => l ? (/s$/i.test(l) ? l : `${l}s`) : null
  const levels = kpis.org_levels || []
  if (levels.length) {
    levels.filter(l => l.units > 0).forEach(l => metrics.push({
      icon: '🏢', label: plural(l.label), value: l.units.toLocaleString(),
      sub: l.people < l.population
        ? `${l.people.toLocaleString()} of ${l.population.toLocaleString()} people assigned`
        : 'Everyone assigned',
      highlight: l.population && l.people / l.population < 0.5 ? 'amber' : null,
    }))
  } else if (kpis.department_count > 0)
    metrics.push({ icon: '🏢', label: plural(kpis.department_label) || 'Departments', value: kpis.department_count.toLocaleString(), sub: null })
  else if (kpis.division_count > 0)
    metrics.push({ icon: '🏢', label: plural(kpis.division_label) || 'Divisions', value: kpis.division_count.toLocaleString(), sub: null })

  return metrics.filter(m => m.value !== '—' && m.value != null).slice(0, 10)
}

export default function OverviewTab({
  customerId, kpis, observations, data_freshness, composition,
  geography, geo_type, pinnedMetrics, setPinnedMetrics, deletePinnedMetric, onTabSwitch, customerMode,
  userName, companyName, tabs, pinnedError = null, onRetryPinned,
}) {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()   // removing a pinned metric is a delete the customer role can't make
  const [medianSalary, setMedianSalary] = useState(null)
  const [multiState, setMultiState] = useState(null)
  // A failed read of these two cards is an error with a Retry -- it used to drop the tiles
  // silently, which reads as "no salaried employees" (item 201). A card the engine
  // answered with no data (no `error`) is a genuine absence and simply isn't shown.
  const [spotlightError, setSpotlightError] = useState(null)
  const [spotlightTry, setSpotlightTry] = useState(0)
  useEffect(() => {
    let live = true
    setMedianSalary(null)
    setMultiState(null)
    setSpotlightError(null)
    runMetrics(customerId, ['median_salary', 'multi_state_employees'])
      .then(rs => {
        if (!live) return
        if (!Array.isArray(rs)) throw new Error('Unexpected response: no list of metric results')
        const failed = []
        // One row per pay currency, most people first: "USD (573)" -> 120000. Currencies
        // are never mixed, so the tile shows the largest one and says how many others exist.
        const r = rs.find(x => x.key === 'median_salary')
        if (!r || r.error) failed.push('Median Salary')
        const rows = Array.isArray(r?.value) ? r.value : []
        const top = rows[0]
        if (r && !r.error && top && top.value != null) {
          const m = String(top.label || '').match(/^([A-Za-z]{3}|Unknown)\s*\((\d+)\)/)
          setMedianSalary({ amount: Number(top.value), currency: m ? m[1] : '', people: m ? Number(m[2]) : null, others: rows.length - 1 })
        }
        const ms = rs.find(x => x.key === 'multi_state_employees')
        if (!ms || ms.error) failed.push('Multi-State Employees')
        if (ms && !ms.error && ms.value != null && !isNaN(Number(ms.value))) setMultiState(Number(ms.value))
        if (failed.length) setSpotlightError(`${failed.join(' and ')} could not be computed.`)
      })
      .catch(err => { if (live) setSpotlightError(loadErrorMessage(err)) })
    return () => { live = false }
  }, [customerId, spotlightTry])
  const spotlightErrorBox = spotlightError && (
    <LoadError inline what="part of the Workforce Snapshot" error={spotlightError}
      onRetry={() => setSpotlightTry(t => t + 1)} />
  )
  const openObs      = (observations||[]).filter(isActive)   // open, acknowledged, fix submitted
  // by tier (findings.tierOf): an FYI finding (informational check) is counted in none
  const critCount    = openObs.filter(o=>tierOf(o)==='critical').length
  const warnCount    = openObs.filter(o=>tierOf(o)==='warning').length
  const infoCount    = openObs.filter(o=>tierOf(o)==='info').length
  const resolvedCount= (observations||[]).filter(o=>!isActive(o)).length
  // Each tab's own numbers, from the function the tab and its tab-bar badge read
  // (findings.tabFindingCounts): the Compliance and Health figures here are the tabs' Open counts.
  const complianceCounts = tabFindingCounts(observations, 'compliance')
  const healthCounts     = tabFindingCounts(observations, 'health')

  // Top findings: critical first, then warnings, up to 5
  const topFindings = useMemo(() => {
    const critical = openObs.filter(o => tierOf(o) === 'critical').slice(0, 3)
    const warnings = openObs.filter(o => tierOf(o) === 'warning').slice(0, 2)
    return [...critical, ...warnings].slice(0, 5)
  }, [openObs])

  const spotlightMetrics = useMemo(
    () => buildSpotlightMetrics(kpis || {}, composition, geo_type, medianSalary, multiState),
    [kpis, composition, geo_type, medianSalary, multiState]
  )


  return (
    <div className="ov-root">

      {/* ── GREETING (customer portal only) ── */}
      {customerMode && (
        <WelcomeBanner
          userName={userName}
          companyName={companyName}
          critCount={critCount}
          warnCount={warnCount}
          freshness={data_freshness}
        />
      )}

      {/* ── TOP ROW (consultant view only) ── */}
      {!customerMode && (
        <div className="ov-top-row">
          <div className="ov-score-card">
            <div className="ov-score-right">
              <div className="ov-score-label">Open Findings</div>
              <div className="ov-score-summary">
                {critCount>0 ? `${critCount} critical issue${critCount!==1?'s':''} need attention`
                  : warnCount>0 ? `${warnCount} warning${warnCount!==1?'s':''} to review`
                  : 'No open critical issues'}
              </div>
              <div className="ov-score-detail">
                {critCount>0   && <span className="ov-sd-pill ov-sd-critical">{critCount} critical</span>}
                {warnCount>0   && <span className="ov-sd-pill ov-sd-warning">{warnCount} warning</span>}
                {infoCount>0   && <span className="ov-sd-pill ov-sd-info">{infoCount} info</span>}
                {resolvedCount>0 && <span className="ov-sd-pill ov-sd-resolved">{resolvedCount} resolved</span>}
                {critCount===0&&warnCount===0&&infoCount===0 && <span className="ov-sd-pill ov-sd-resolved">All clear</span>}
              </div>
            </div>
          </div>
          <div className="ov-stats">
            <div className="ov-stat" onClick={()=>onTabSwitch('compliance')} style={{cursor:'pointer'}}>
              <div className={`ov-stat-val ${complianceCounts.open>0?'red':'green'}`}>{complianceCounts.open}</div>
              <div className="ov-stat-label">Compliance Issues</div>
              <div className="ov-stat-link">View Compliance →</div>
            </div>
            <div className="ov-stat" onClick={()=>onTabSwitch('health')} style={{cursor:'pointer'}}>
              <div className={`ov-stat-val ${healthCounts.open>0?'amber':'green'}`}>{healthCounts.open}</div>
              <div className="ov-stat-label">Health Issues</div>
              <div className="ov-stat-link">View Health →</div>
            </div>
            <div className="ov-stat">
              <div className="ov-stat-val blue">{kpis.active_count?.toLocaleString()??'—'}</div>
              <div className="ov-stat-label">Active Employees</div>
              <div className="ov-stat-sub">{kpis.state_count} {geo_type==='countries'?'countries':'states'}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── METRIC SPOTLIGHT (portal only): first, Top Findings directly below (Scott,
          2026-10-05). The consultant version sits above its swimlanes, further down. ── */}
      {customerMode && (spotlightMetrics.length > 0 || spotlightError) && (
        <div className="ov-spotlight-section">
          <div className="ov-section-header">
            <span className="ov-section-title">Workforce Snapshot</span>
            <button className="ov-section-link" onClick={() => onTabSwitch('metrics')}>View metrics →</button>
          </div>
          {spotlightErrorBox}
          <div className="ov-spotlight-grid">
            {spotlightMetrics.map((m, i) => (
              <div key={i} className={`ov-spotlight-card ${m.highlight ? `ov-spotlight-${m.highlight}` : ''}`}>
                <div className="ov-spotlight-icon">{m.icon}</div>
                <div className="ov-spotlight-value">{m.value}</div>
                <div className="ov-spotlight-label">{m.label}</div>
                {m.sub && <div className="ov-spotlight-sub">{m.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TOP FINDINGS (customer portal only) ── */}
      {customerMode && (
        <div className="ov-findings-section">
          <div className="ov-section-header">
            <span className="ov-section-title">
              {topFindings.length > 0 ? 'Top Findings' : 'Findings'}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ov-section-link" onClick={() => onTabSwitch('compliance')}>Compliance →</button>
              <button className="ov-section-link" onClick={() => onTabSwitch('health')}>Health →</button>
            </div>
          </div>
          {topFindings.length === 0 ? (
            <div className="ov-findings-clear">
              <span className="ov-findings-clear-icon">✓</span>
              No open findings — all checks passing.
            </div>
          ) : (
            <div className="ov-findings-list">
              {topFindings.map((obs, i) => (
                <FindingRow key={obs.id || i} obs={obs} onTabSwitch={onTabSwitch} />
              ))}
              {/* the rows shown can be fewer than 5 (up to 3 critical + 2 warning) */}
              {openObs.length > topFindings.length && (
                <div className="ov-findings-more">
                  +{openObs.length - topFindings.length} more findings — <button className="ov-section-link" onClick={() => onTabSwitch('health')}>view all →</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── METRIC SPOTLIGHT (consultant: above swimlanes for visual prominence) ── */}
      {!customerMode && (spotlightMetrics.length > 0 || spotlightError) && (
        <div className="ov-spotlight-section">
          <div className="ov-section-header">
            <span className="ov-section-title">Workforce Snapshot</span>
            <button className="ov-section-link" onClick={() => onTabSwitch('metrics')}>All metrics →</button>
          </div>
          {spotlightErrorBox}
          <div className="ov-spotlight-grid">
            {spotlightMetrics.map((m, i) => (
              <div key={i} className={`ov-spotlight-card ${m.highlight ? `ov-spotlight-${m.highlight}` : ''}`}>
                <div className="ov-spotlight-icon">{m.icon}</div>
                <div className="ov-spotlight-value">{m.value}</div>
                <div className="ov-spotlight-label">{m.label}</div>
                {m.sub && <div className="ov-spotlight-sub">{m.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CONSULTANT SWIMLANES (consultant view only) ── */}
      {!customerMode && (
        <div className="ov-swimlanes">
          <div className="ov-lane">
            <div className="ov-lane-header" style={{cursor:'default'}}>
              <div className="ov-lane-title"><span className="ov-lane-icon">⚖</span> Compliance {complianceCounts.open>0&&<span className="ov-lane-badge ov-lb-red">{complianceCounts.open}</span>}</div>
              <button className="ov-lane-link" onClick={()=>onTabSwitch('compliance')}>View all →</button>
            </div>
            {complianceCounts.active.slice(0,4).length === 0
              ? <div className="ov-swim-row ov-swim-clear"><span className="ov-swim-dot ov-dot-clear">✓</span><span className="ov-swim-title">No open compliance issues</span><span className="ov-swim-detail">All checks passing</span></div>
              : complianceCounts.active.slice(0,4).map((o,i)=>(
                  <div key={o.id||i} className={`ov-swim-row ov-swim-${o.type==='risk'?'critical':o.type==='insight'?'warning':'info'}`} onClick={()=>onTabSwitch('compliance')} style={{cursor:'pointer'}}>
                    <span className={`ov-swim-dot ov-dot-${o.type==='risk'?'critical':o.type==='insight'?'warning':'info'}`}>●</span>
                    <span className="ov-swim-title">{o.title}</span>
                    <span className="ov-swim-detail">{(o.detail||'').slice(0,80)}</span>
                    <span className="ov-swim-arrow">›</span>
                  </div>
                ))
            }
          </div>
          <div className="ov-lane">
            <div className="ov-lane-header" style={{cursor:'default'}}>
              <div className="ov-lane-title"><span className="ov-lane-icon">⚙</span> Health {healthCounts.open>0&&<span className="ov-lane-badge ov-lb-amber">{healthCounts.open}</span>}</div>
              <button className="ov-lane-link" onClick={()=>onTabSwitch('health')}>View all →</button>
            </div>
            {healthCounts.active.slice(0,4).length === 0
              ? <div className="ov-swim-row ov-swim-clear"><span className="ov-swim-dot ov-dot-clear">✓</span><span className="ov-swim-title">No open health issues</span><span className="ov-swim-detail">Configuration quality checks passing</span></div>
              : healthCounts.active.slice(0,4).map((o,i)=>(
                  <div key={o.id||i} className={`ov-swim-row ov-swim-${o.type==='risk'?'critical':o.type==='insight'?'warning':'info'}`} onClick={()=>onTabSwitch('health')} style={{cursor:'pointer'}}>
                    <span className={`ov-swim-dot ov-dot-${o.type==='risk'?'critical':o.type==='insight'?'warning':'info'}`}>●</span>
                    <span className="ov-swim-title">{o.title}</span>
                    <span className="ov-swim-detail">{(o.detail||'').slice(0,80)}</span>
                    <span className="ov-swim-arrow">›</span>
                  </div>
                ))
            }
          </div>
        </div>
      )}

      {/* ── DATA SOURCES ── */}
      <div className="ov-bottom-row">
        <div className="ov-sidebar ov-sidebar-bottom">
          <div className="ov-sidebar-card">
            <div className="ov-sidebar-header">
              <span className="ov-sidebar-title">Data Sources</span>
              {data_freshness?.as_of && <span className="ov-sidebar-meta">Data as of {fmtSyncDate(data_freshness.as_of)}</span>}
            </div>
            {data_freshness?.systems?.length>0 ? (
              <div className="ov-src-table-wrap">
                <table className="ov-src-table">
                  <thead><tr><th>Source</th><th>Data</th><th>Last synced</th><th>Status</th></tr></thead>
                  <tbody>{data_freshness.systems.map(sys=><SystemRow key={sys.label} system={sys}/>)}</tbody>
                </table>
              </div>
            ) : <div className="ov-sidebar-empty">No data sources connected</div>}
          </div>
        </div>
      </div>

      {/* ── PINNED METRICS ── a failed read is an error with a Retry, never no section */}
      {pinnedError && (
        <div className="ov-pinned-section">
          <LoadError what="pinned metrics" error={pinnedError} onRetry={onRetryPinned} />
        </div>
      )}
      {!pinnedError && pinnedMetrics?.length>0&&(
        <div className="ov-pinned-section">
          <div className="ov-pinned-header">
            <span className="ov-pinned-title">📌 Pinned Metrics</span>
            <span className="ov-pinned-meta">{pinnedMetrics.length} from chat</span>
          </div>
          <div className="cp-pinned-grid">
            {pinnedMetrics.map(m=>(
              <div key={m.id} className={`cp-pinned-card ${m.error?'error':''}`}>
                <div className="cp-pinned-top">
                  <span className="cp-pinned-icon">{m.icon||'📊'}</span>
                  {isAdmin && (<button className="cp-pinned-remove" onClick={async()=>{
                    if(!window.confirm(`Remove "${m.label}"?`))return
                    try{await deletePinnedMetric(customerId,m.id);setPinnedMetrics(prev=>prev.filter(x=>x.id!==m.id))}catch(e){console.error(e)}
                  }}>✕</button>)}
                </div>
                {m.error?<div className="cp-pinned-error">⚠ Query failed</div>:(
                  <div className="cp-pinned-value">
                    {m.display_type==='currency'?`$${Number(m.value||0).toLocaleString()}`
                     :m.display_type==='percent'?`${Number(m.value||0).toFixed(1)}%`
                     :typeof m.value==='number'?m.value.toLocaleString(undefined,{maximumFractionDigits:1})
                     :String(m.value??'—')}
                  </div>
                )}
                <div className="cp-pinned-label">{m.label}</div>
                {m.source_question&&<div className="cp-pinned-question">"{m.source_question.slice(0,60)}{m.source_question.length>60?'…':''}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
